import {ChangeDetectionStrategy, Component, computed, inject, input, Input} from '@angular/core';
import { CommonModule } from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {ModelDetail} from "@interface/model-detail/model-detail";
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'app-composer-card',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="
        ipe:border ipe:border-[#f9f8f6] ipe:flex ipe:flex-col ipe:justify-between
        ipe:group ipe:block ipe:h-full ipe:bg-white ipe:overflow-hidden
        ipe:transition ipe:duration-200
        ipe:hover:-translate-y-0.5 ipe:hover:shadow-sm ipe:hover:ring-1 ipe:hover:ring-[#f9f8f6]/10
        ipe:focus-visible:outline-none ipe:focus-visible:ring-2 ipe:focus-visible:ring-[#f9f8f6]/20
        ipe:cursor-pointer
        ipe:no-underline
      "
      (click)="goToComposer($event)"
      aria-label="Componi il tuo Walker nel configuratore 3D"
    >
      <div class="flex-grow-1 ipe:relative ipe:aspect-[16/10] ipe:bg-white">
        <div class="ipe:absolute ipe:top-4 ipe:left-4 ipe:z-10">
          <span
            class="
              ipe:inline-flex ipe:items-center ipe:gap-2
              ipe:rounded-md ipe:bg-[#d6cdc0] ipe:px-3 ipe:py-1
              ipe:text-sm ipe:font-semibold
            "
          >
            <span class="ipe:h-2 ipe:w-2 ipe:rounded-full ipe:bg-emerald-500 ipe:text-white"></span>
            <span>{{ 'composition.componibile' | translate }}</span>
          </span>
        </div>
        <div class="ipe:h-full ipe:flex ipe:flex-col ipe:items-center ipe:justify-center ipe:px-8 ipe:text-center">
          <div
            class="
              ipe:mb-4 ipe:h-11 ipe:w-11 ipe:rounded-2xl
              ipe:bg-[#d6cdc0] ipe:text-white
              ipe:flex ipe:items-center ipe:justify-center
              group-hover:ipe:scale-[1.03] ipe:transition text-white
            "
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" class="ipe:h-5 ipe:w-5 text-white" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2 3 7l9 5 9-5-9-5Z"></path>
              <path d="M3 7v10l9 5 9-5V7"></path>
              <path d="M12 12v10"></path>
            </svg>
          </div>
          <h3 class="ipe:text-base ipe:font-semibold ipe:text-gray-900">
            {{ 'composition.composeYour' | translate }} {{ vm().title }}
          </h3>
          <p class="ipe:mt-2 ipe:text-sm ipe:text-gray-600 ipe:leading-relaxed ipe:max-w-[30ch]">
            {{ 'composition.subtitle' | translate }}
          </p>
          <div class="ipe:mt-6 ipe:inline-flex ipe:items-center ipe:gap-2 ipe:text-sm ipe:font-semibold ipe:text-gray-900">
            {{ 'composition.openConfigurator' | translate }}
            <span class="group-hover:ipe:translate-x-0.5 ipe:transition" aria-hidden="true">→</span>
          </div>
        </div>
      </div>
      <div class="ipe:bg-[#d6cdc0] ipe:px-4 ipe:pb-4 ipe:pt-3 ipe:flex ipe:items-start ipe:justify-between ipe:gap-3 ipe:border-t ipe:border-gray-100">
        <div class="ipe:min-w-0">
          <div class="ipe:text-sm ipe:font-semibold  ipe:tracking-wide">
            {{ vm().category }}
          </div>
          <div class="ipe:mt-1 ipe:text-sm">
            {{ 'composition.composer-3d' | translate }}
          </div>
        </div>
        <div
          class="
            ipe:shrink-0 ipe:h-9 ipe:w-9 ipe:rounded-xl
            ipe:flex ipe:items-center ipe:justify-center
            ipe:bg-gray-50 group-hover:ipe:bg-gray-100 ipe:transition
          "
          aria-hidden="true"
          title="Apri"
        >
          <svg viewBox="0 0 24 24" class="ipe:h-4 ipe:w-4 ipe:text-gray-900" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M7 17 17 7"></path>
            <path d="M9 7h8v8"></path>
          </svg>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @import 'tailwindcss' prefix(ipe);
    :host { display: block; height: 100%; }
  `],
})
export class ComposerCardComponent {
  private readonly router = inject(Router);
  readonly model = input.required<ModelDetail>();
  readonly vm = computed(() => {
    const m = this.model();
    const slug = m.slugModular?.trim() || null;
    return {
      title: m.modelName?.trim() || 'Configurator',
      subtitle:
        m.modelDescription?.trim() ||
        'Crea la configurazione perfetta: moduli, dimensioni e layout in tempo reale.',
      category: m.modelNameId?.trim() || '',
      academyUrl: m.academyUrl?.trim() || null,
      slug,
      urlTree: slug ? this.router.createUrlTree(['model-composer', slug]) : null,
    };
  });

  goToComposer(ev?: MouseEvent): void {
    ev?.preventDefault();
    const tree = this.vm().urlTree;
    if (!tree) {
      console.warn('[ComposerCard] Missing slugModular', this.model());
      return;
    }
    this.router.navigateByUrl(tree).catch(err => console.error('[ComposerCard] Navigation error', err));
  }
}
